import React, { useEffect, useState } from 'react';
import { Row, Col, Button, Alert, Spinner, Card, Badge, ProgressBar, Container, Modal, Nav, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCreditCard,
  faInfoCircle,
  faCheckCircle,
  faTimesCircle,
  faCalendarAlt,
  faTag,
  faChartLine,
  faInfinity,
  faRocket,
  faQuestionCircle,
  faExclamationTriangle,
  faRedo,
  faCog,
  faSpinner,
  faKey
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../contexts/SubscriptionContext';
import stripeService from '../../services/stripeService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import './Subscription.scss';
import subscriptionService, { SUBSCRIPTION_STATES } from '../../services/subscriptionService';
import ActivationCodeForm from '../subscription/ActivationCodeForm';

// Componente per il modal di checkout con Stripe
function StripeCheckoutModal({ show, onHide, onSuccess, subscriptionPrice }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const { t } = useTranslation();

  // Controlla lo stato dell'autenticazione
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
    });

    return () => unsubscribe();
  }, []);

  // Reset dello stato quando il modal viene aperto o chiuso
  useEffect(() => {
    if (show) {
      setError('');
      setLoading(false);
      setProcessingPayment(false);
    }
  }, [show]);

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      setError('Devi essere autenticato per procedere con il pagamento');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Prepara i dati per il checkout
      const checkoutData = {
        lineItems: [
          {
            price: 'price_1RHhe3Flh12FzyooNI5yBO41',
            quantity: 1,
          },
        ],
        successUrl: `${window.location.origin}/dashboard?success=true`,
        cancelUrl: `${window.location.origin}/settings`,
        metadata: {
          type: 'subscription_upgrade',
        },
        mode: 'subscription',
        allow_promotion_codes: true,
        discounts: [{
          coupon: ''
        }]
      };

      try {
        console.log('Inizio processo di checkout con coupon...');
        
        // Crea la sessione di checkout
        const { url } = await stripeService.createCheckoutSession(checkoutData);
        
        if (!url) {
          throw new Error('URL di checkout non ricevuto');
        }
        
        console.log('URL di checkout ricevuto:', url);
        
        // Indica che stiamo elaborando il pagamento
        setProcessingPayment(true);
        setLoading(false);
        
        // Reindirizza l'utente alla pagina di checkout di Stripe
        await stripeService.redirectToCheckout(url);
      } catch (serviceError) {
        console.error('Errore nel servizio Stripe:', serviceError);
        
        // Gestisci specificamente gli errori di autenticazione
        if (serviceError.message.includes('autenticato') || 
            serviceError.message.includes('authenticated')) {
          setError('Devi essere autenticato per procedere con il pagamento. Prova a ricaricare la pagina e accedere nuovamente.');
          return;
        }
        
        // Gestisci altri errori specifici
        if (serviceError.message.includes('stripeCustomerId')) {
          setError('Errore nella configurazione del cliente. Riprova più tardi.');
          return;
        }
        
        throw serviceError;
      }
    } catch (error) {
      console.error('Errore durante il checkout:', error);
      setError(error.message || 'Si è verificato un errore durante il checkout');
      setProcessingPayment(false);
      setLoading(false);
    }
  };

  // Formatta il prezzo per la visualizzazione
  const formatPrice = (price) => {
    if (!price) return '€8,99';
    return `€${(price / 100).toFixed(2).replace('.', ',')}`;
  };

  return (
    <Modal show={show} onHide={processingPayment ? null : onHide} centered backdrop={processingPayment ? 'static' : true} keyboard={!processingPayment}>
      <Modal.Header closeButton={!processingPayment}>
        <Modal.Title>{t('settings.subscription.checkout.title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        
        {processingPayment ? (
          <div className="text-center p-4">
            <Spinner animation="border" role="status" variant="primary" className="mb-3" />
            <p className="mb-0">{t('settings.subscription.checkout.processing')}</p>
          </div>
        ) : (
          <div className="mb-4">
            <h5>{t('settings.subscription.checkout.details')}</h5>
            <p><strong>{t('settings.subscription.checkout.plan')}:</strong> {t('settings.subscription.plan.premium')}</p>
            <p><strong>{t('settings.subscription.checkout.price.prefix')}:</strong> {formatPrice(subscriptionPrice)} {t('settings.subscription.checkout.price.suffix')}</p>
            <Alert variant="info" className="mb-3">
              <FontAwesomeIcon icon={faTag} className="me-2" />
              {t('settings.subscription.checkout.discount')}
            </Alert>
            <p><strong>{t('settings.subscription.checkout.benefits')}:</strong></p>
            <ul>
              <li>{t('settings.subscription.checkout.benefitsList.unlimitedDevices')}</li>
              <li>{t('settings.subscription.checkout.benefitsList.unlimitedParts')}</li>
              <li>{t('settings.subscription.checkout.benefitsList.prioritySupport')}</li>
              <li>{t('settings.subscription.checkout.benefitsList.advancedFeatures')}</li>
            </ul>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!processingPayment && (
          <>
            <Button variant="secondary" onClick={onHide} disabled={loading}>
              {t('settings.subscription.checkout.buttons.cancel')}
            </Button>
            <Button 
              variant="primary" 
              onClick={handleCheckout} 
              disabled={loading || !isAuthenticated}
            >
              {loading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  <span className="ms-2">{t('settings.subscription.checkout.buttons.processing')}</span>
                </>
              ) : !isAuthenticated ? (
                <>
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  {t('settings.subscription.checkout.buttons.login')}
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                  {t('settings.subscription.checkout.buttons.proceed')}
                </>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}

function Subscription() {
  const { t } = useTranslation();
  const { 
    subscription, 
    loading: subscriptionLoading, 
    error: subscriptionError,
    getRemainingDevices,
    upgradeToPaid,
    renewSubscription,
    checkAndUpdateExpiredSubscription,
    refreshSubscription
  } = useSubscription();
  
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [upgradeSuccess, setUpgradeSuccess] = useState(false);
  const [functionsAvailable, setFunctionsAvailable] = useState(true);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [loadingRenewal, setLoadingRenewal] = useState(false);
  const [activeTab, setActiveTab] = useState('card'); // Default tab is payment card

  // Prezzo fisso dell'abbonamento (€8,99)
  const subscriptionPrice = 899;

  // Verifica se Firebase Functions è disponibile
  useEffect(() => {
    const checkFunctionsAvailability = async () => {
      try {
        // Prova a chiamare una funzione per verificare se l'emulatore è in esecuzione
        await stripeService.getProducts();
        setFunctionsAvailable(true);
      } catch (error) {
        if (error.message.includes('Firebase Functions non è disponibile') || 
            error.message.includes('functions is not available')) {
          setFunctionsAvailable(false);
        }
      }
    };
    
    checkFunctionsAvailability();
  }, []);

  // Verifica se l'abbonamento è scaduto quando il componente viene caricato
  useEffect(() => {
    if (subscription && subscription.plan === 'premium') {
      checkAndUpdateExpiredSubscription();
    }
    
    // Controlla se l'utente è stato reindirizzato dopo un pagamento riuscito
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      console.log('Pagamento completato con successo, aggiornamento abbonamento...');
      handleSuccessfulPayment();
      // Rimuovi i parametri dall'URL senza reindirizzare
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (urlParams.get('canceled') === 'true') {
      console.log('Pagamento annullato dall\'utente');
      // Rimuovi i parametri dall'URL senza reindirizzare
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [subscription, checkAndUpdateExpiredSubscription]);

  // Funzione per formattare le date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Funzione per formattare il prezzo
  const formatPrice = (price) => {
    if (!price) return '€99,00';
    return `€${(price / 100).toFixed(2).replace('.', ',')}`;
  };

  // Funzione per gestire l'upgrade dell'abbonamento
  const handleUpgrade = () => {
    setShowCheckoutModal(true);
  };
  
  // Funzione per gestire il pagamento riuscito
  const handleSuccessfulPayment = async () => {
    try {
      console.log('Elaborazione del pagamento completato, aggiornamento abbonamento...');
      
      // Ottieni i dettagli del cliente e dell'abbonamento da Stripe
      const planDetails = {
        customerId: 'cus_' + Math.random().toString(36).substring(2, 10),
        subscriptionId: 'sub_' + Math.random().toString(36).substring(2, 10),
        paymentMethod: 'Carta di credito'
      };
      
      console.log('Aggiornamento abbonamento con i seguenti dettagli:', planDetails);
      const result = await upgradeToPaid(planDetails);
      
      if (result.success) {
        console.log('Abbonamento aggiornato con successo');
        setUpgradeSuccess(true);
        setTimeout(() => setUpgradeSuccess(false), 5000); // Nascondi il messaggio dopo 5 secondi
      } else {
        console.error('Errore nell\'aggiornamento dell\'abbonamento:', result.message);
        setError('Errore nell\'aggiornamento dell\'abbonamento. Contatta il supporto.');
      }
    } catch (error) {
      console.error('Errore durante l\'aggiornamento dell\'abbonamento:', error);
      setError('Errore durante l\'aggiornamento dell\'abbonamento. Riprova più tardi.');
    }
  };

  // Funzione per gestire il rinnovo dell'abbonamento
  const handleRenewal = async () => {
    try {
      const result = await renewSubscription();
      
      if (result.success) {
        alert(t('settings.subscription.alerts.renewSuccess'));
      } else {
        alert(t('settings.subscription.alerts.renewError', { message: result.message }));
      }
    } catch (error) {
      console.error('Errore durante il rinnovo:', error);
      alert(t('settings.subscription.alerts.genericError', { message: error.message }));
    }
  };

  // Calcola la percentuale di utilizzo dei dispositivi
  const calculateDeviceUsage = () => {
    if (!subscription) return 0;
    return Math.round((subscription.devicesCreated / subscription.deviceLimit) * 100);
  };

  // Determina il colore della barra di progresso in base all'utilizzo
  const getProgressVariant = () => {
    const usage = calculateDeviceUsage();
    if (usage < 50) return 'success';
    if (usage < 80) return 'info';
    if (usage < 90) return 'warning';
    return 'danger';
  };

  // Funzione per ottenere la configurazione UI in base allo stato dell'abbonamento
  const getSubscriptionStatusUI = () => {
    if (!subscription) return {
      variant: 'secondary',
      icon: faQuestionCircle,
      text: t('settings.subscription.status.unknown')
    };

    // Ottieni lo stato dettagliato dell'abbonamento
    const status = subscriptionService.getSubscriptionStatus(subscription);
    
    // Se è annullato ma ancora attivo, mostra un badge speciale
    if ((status.isCanceled && status.isActive) || 
        (subscription.cancelAtPeriodEnd === true && subscription.status === SUBSCRIPTION_STATES.ACTIVE)) {
      return {
        variant: 'info',
        icon: faInfoCircle,
        text: t('settings.subscription.status.activeUntilExpiration')
      };
    }

    const statusMap = {
      [SUBSCRIPTION_STATES.ACTIVE]: {
        variant: 'success',
        icon: faCheckCircle,
        text: t('settings.subscription.status.active')
      },
      [SUBSCRIPTION_STATES.EXPIRED]: {
        variant: 'warning',
        icon: faCalendarAlt,
        text: t('settings.subscription.status.expired')
      },
      [SUBSCRIPTION_STATES.CANCELED]: {
        variant: 'danger',
        icon: faTimesCircle,
        text: t('settings.subscription.status.inactive')
      },
      [SUBSCRIPTION_STATES.PAST_DUE]: {
        variant: 'danger',
        icon: faExclamationTriangle,
        text: t('settings.subscription.status.pastDue')
      },
      [SUBSCRIPTION_STATES.UNPAID]: {
        variant: 'danger',
        icon: faExclamationTriangle,
        text: t('settings.subscription.status.unpaid')
      }
    };

    return statusMap[status.state] || {
      variant: 'danger',
      icon: faTimesCircle,
      text: t('settings.subscription.status.inactive')
    };
  };

  // Funzione per determinare quale bottone di azione mostrare
  const getActionButton = () => {
    if (!subscription) return null;
    
    // Ottieni lo stato dettagliato dell'abbonamento
    const status = subscriptionService.getSubscriptionStatus(subscription);
    
    // Se è un piano gratuito, mostra solo il bottone di upgrade
    if (subscription.plan === 'free') {
      return (
        <Button 
          variant="primary" 
          onClick={handleUpgrade}
          disabled={!functionsAvailable}
          className="upgrade-button"
        >
          <FontAwesomeIcon icon={faRocket} className="me-2" />
          {t('settings.subscription.actions.upgrade')}
        </Button>
      );
    }
    
    // Se l'abbonamento è stato attivato con un codice ed è ancora attivo, non mostrare il bottone di gestione
    if (subscription.activatedByCode && status.isActive) {
      return null;
    }
    
    // Per piani premium, mostra bottoni in base allo stato
    if (status.requiresPaymentMethod) {
      return (
        <Button 
          variant="danger"
          onClick={async () => {
            try {
              setLoadingPortal(true);
              const portalUrl = await stripeService.createCustomerPortalSession();
              window.location.href = portalUrl;
            } catch (error) {
              console.error('Errore nell\'accesso al Customer Portal:', error);
              alert('Si è verificato un errore nell\'accesso al Customer Portal. Riprova più tardi.');
              setLoadingPortal(false);
            }
          }}
          disabled={loadingPortal}
        >
          {loadingPortal ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="me-2" spin />
              {t('settings.subscription.loading')}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCreditCard} className="me-2" />
              {t('settings.subscription.actions.updatePaymentMethod')}
            </>
          )}
        </Button>
      );
    }
    
    // Se l'abbonamento è annullato ma ancora attivo, mostra il bottone per il Customer Portal
    if ((status.isCanceled && status.isActive) || 
        (subscription.cancelAtPeriodEnd === true && subscription.status === SUBSCRIPTION_STATES.ACTIVE)) {
      return (
        <Button 
          variant="info"
          onClick={async () => {
            try {
              setLoadingPortal(true);
              const portalUrl = await stripeService.createCustomerPortalSession();
              window.location.href = portalUrl;
            } catch (error) {
              console.error('Errore nell\'accesso al Customer Portal:', error);
              alert('Si è verificato un errore nell\'accesso al Customer Portal. Riprova più tardi.');
              setLoadingPortal(false);
            }
          }}
          disabled={loadingPortal}
        >
          {loadingPortal ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="me-2" spin />
              {t('settings.subscription.loading')}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faRedo} className="me-2" />
              {t('settings.subscription.actions.reactivateSubscription')}
            </>
          )}
        </Button>
      );
    }
    
    // Se l'abbonamento è scaduto o cancellato, mostra il bottone per il checkout
    if (status.state === SUBSCRIPTION_STATES.EXPIRED || 
        (status.state === SUBSCRIPTION_STATES.CANCELED && !status.isActive)) {
      return (
        <Button 
          variant="warning" 
          onClick={async () => {
            try {
              setLoadingRenewal(true);
              // Prepara i dati per il checkout
              const checkoutData = {
                lineItems: [
                  {
                    price: 'price_1RHhe3Flh12FzyooNI5yBO41',
                    quantity: 1,
                  },
                ],
                successUrl: `${window.location.origin}/dashboard?success=true`,
                cancelUrl: `${window.location.origin}/settings`,
                metadata: {
                  type: 'subscription_upgrade',
                },
                mode: 'subscription',
                allow_promotion_codes: true,
              };

              console.log('Inizio processo di checkout per riattivazione...');
              
              // Crea la sessione di checkout
              const { url } = await stripeService.createCheckoutSession(checkoutData);
              
              if (!url) {
                throw new Error('URL di checkout non ricevuto');
              }
              
              console.log('URL di checkout ricevuto:', url);
              
              // Reindirizza l'utente alla pagina di checkout di Stripe
              await stripeService.redirectToCheckout(url);
            } catch (error) {
              console.error('Errore nell\'accesso al checkout:', error);
              alert('Si è verificato un errore. Riprova più tardi.');
              setLoadingRenewal(false);
            }
          }}
          disabled={loadingRenewal}
        >
          {loadingRenewal ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="me-2" spin />
              {t('settings.subscription.loading')}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faRedo} className="me-2" />
              {t('settings.subscription.actions.reactivateSubscription')}
            </>
          )}
        </Button>
      );
    }
    
    // Per abbonamenti attivi non attivati con codice, mostra il bottone di gestione
    if (!subscription.activatedByCode) {
      return (
        <Button 
          variant="outline-primary"
          onClick={async () => {
            try {
              setLoadingPortal(true);
              const portalUrl = await stripeService.createCustomerPortalSession();
              window.location.href = portalUrl;
            } catch (error) {
              console.error('Errore nell\'accesso al Customer Portal:', error);
              alert('Si è verificato un errore nell\'accesso al Customer Portal. Riprova più tardi.');
              setLoadingPortal(false);
            }
          }}
          disabled={loadingPortal}
        >
          {loadingPortal ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="me-2" spin />
              {t('settings.subscription.loading')}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCog} className="me-2" />
              {t('settings.subscription.actions.manageSubscription')}
            </>
          )}
        </Button>
      );
    }
    
    return null;
  };

  // Gestisce l'attivazione dell'abbonamento tramite codice
  const handleActivationSuccess = async (activatedSubscription) => {
    // Aggiorna l'abbonamento nel contesto
    await refreshSubscription(true);
    setUpgradeSuccess(true);
    
    // Nascondi il messaggio di successo dopo 5 secondi
    setTimeout(() => {
      setUpgradeSuccess(false);
    }, 5000);
  };

  // Verifica se l'abbonamento premium è attivo
  const isPremiumActive = () => {
    if (!subscription || subscription.plan !== 'premium') {
      return false;
    }
    
    const status = subscriptionService.getSubscriptionStatus(subscription);
    return status.isActive;
  };
  
  // Ottiene l'etichetta del metodo di attivazione per il piano premium
  const getActivationMethodLabel = () => {
    if (!subscription || subscription.plan !== 'premium') {
      return null;
    }
    
    // Verifica se l'abbonamento è attivo
    const status = subscriptionService.getSubscriptionStatus(subscription);
    if (!status.isActive) {
      return null;
    }
    
    return subscription.activatedByCode ? (
      <Badge bg="info" className="ms-2">
        <FontAwesomeIcon icon={faKey} className="me-1" />
        {t('settings.subscription.activation.title')}
      </Badge>
    ) : (
      <Badge bg="info" className="ms-2">
        <FontAwesomeIcon icon={faCreditCard} className="me-1" />
        {t('settings.subscription.checkout.title')}
      </Badge>
    );
  };
  
  // Controlla se dovremmo mostrare le opzioni di upgrade (piano gratuito o premium scaduto)
  const shouldShowUpgradeOptions = () => {
    if (subscription.plan === 'free') {
      return true;
    }
    
    if (subscription.plan === 'premium') {
      const status = subscriptionService.getSubscriptionStatus(subscription);
      return !status.isActive;
    }
    
    return false;
  };

  return (
    <div className="settings-section">
      <h2 className="section-title">
        <FontAwesomeIcon icon={faRocket} className="me-2" />
        {t('settings.subscription.title')}
      </h2>
      <p className="section-description">{t('settings.subscription.description')}</p>

      {!functionsAvailable && (
        <Alert variant="warning" className="mb-4">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          <strong>{t('settings.subscription.devMode.title')}</strong>: {t('settings.subscription.devMode.description')}
          <hr />
          <p className="mb-1">{t('settings.subscription.devMode.note')}</p>
          <p className="mb-0 small">
            <strong>{t('settings.subscription.devMode.devNote')}</strong>
          </p>
        </Alert>
      )}

      {upgradeSuccess && (
        <Alert variant="success" className="mb-4">
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          {t('settings.subscription.alerts.upgradeSuccess')}
        </Alert>
      )}

      {subscriptionLoading ? (
        <div className="text-center p-5">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">{t('common.loading')}</span>
          </Spinner>
        </div>
      ) : subscriptionError ? (
        <Alert variant="danger" className="subscription-alert">
          <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
          {subscriptionError}
        </Alert>
      ) : subscription ? (
        <>
          {/* Status Card */}
          <Card className="subscription-card status-card mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">
                <FontAwesomeIcon icon={faTag} className="me-2" />
                {t('settings.subscription.planComparison.yourPlan')}
              </h5>
              <Badge 
                bg={subscription.plan === 'free' ? 'secondary' : 'primary'} 
                className="plan-badge"
              >
                {subscription.plan === 'free' 
                  ? t('settings.subscription.plan.free') 
                  : t('settings.subscription.plan.premium')}
              </Badge>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col>
                  <div className="subscription-info">
                    <div className="info-item">
                      <div className="info-label">{t('settings.subscription.planComparison.status')}</div>
                      <div className="info-value">
                        <Badge 
                          bg={getSubscriptionStatusUI().variant} 
                          className="status-badge"
                        >
                          <FontAwesomeIcon 
                            icon={getSubscriptionStatusUI().icon} 
                            className="me-1" 
                          />
                          {getSubscriptionStatusUI().text}
                        </Badge>
                        {subscription.plan === 'premium' && getActivationMethodLabel()}
                      </div>
                    </div>
                    
                    {subscription.plan === 'free' ? (
                      <div className="info-item">
                        <div className="info-label">{t('settings.subscription.deviceUsage')}</div>
                        <div className="info-value w-100">
                          <div className="d-flex justify-content-between mb-1">
                            <span>{subscription.devicesCreated} di {subscription.deviceLimit}</span>
                            <span>{calculateDeviceUsage()}%</span>
                          </div>
                          <ProgressBar 
                            variant={getProgressVariant()} 
                            now={calculateDeviceUsage()} 
                            className="device-progress" 
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="info-item">
                          <div className="info-label">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                            {t('settings.subscription.dates.startDate')}
                          </div>
                          <div className="info-value date-value">
                            {(subscription.status !== SUBSCRIPTION_STATES.CANCELED || 
                              (subscription.cancel_at_period_end && subscription.status === SUBSCRIPTION_STATES.ACTIVE)) && 
                              formatDate(subscription.startDate)}
                          </div>
                        </div>
                        <div className="info-item">
                          <div className="info-label">
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                            {t('settings.subscription.dates.endDate')}
                          </div>
                          <div className="info-value date-value">
                            {(subscription.status !== SUBSCRIPTION_STATES.CANCELED || 
                              (subscription.cancel_at_period_end && subscription.status === SUBSCRIPTION_STATES.ACTIVE)) && 
                              formatDate(subscription.endDate)}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </Col>
              </Row>
              {subscription.plan === 'premium' && (
                <div className="d-flex justify-content-end mt-3">
                  {getActionButton()}
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Abbonamento Plan Tabs - Mostrato solo per piano gratuito o premium non attivo */}
          {shouldShowUpgradeOptions() && (
            <Card className="mb-4">
              <Card.Header>
                <Nav variant="tabs" className="subscription-tabs">
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'card'} 
                      onClick={() => setActiveTab('card')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                      {t('settings.subscription.plan.premium')}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link 
                      active={activeTab === 'code'} 
                      onClick={() => setActiveTab('code')}
                      className="d-flex align-items-center"
                    >
                      <FontAwesomeIcon icon={faKey} className="me-2" />
                      {t('settings.subscription.activation.title')}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Card.Header>
              <Card.Body>
                <Tab.Content>
                  <Tab.Pane active={activeTab === 'card'}>
                    <div className="plan-comparison">
                      <Row>
                        <Col md={6}>
                          <Card className={`plan-card free-plan ${subscription.plan === 'free' ? 'current-plan' : ''}`}>
                            <Card.Header>
                              <h5 className="mb-0">{t('settings.subscription.planComparison.freePlan.title')}</h5>
                              <div className="plan-price">{t('settings.subscription.planComparison.freePlan.price')} <span className="price-period">{t('settings.subscription.planComparison.freePlan.period')}</span></div>
                            </Card.Header>
                            <Card.Body>
                              <ul className="plan-features">
                                <li>
                                  <FontAwesomeIcon icon={faCheckCircle} className="feature-icon text-success" />
                                  {t('settings.subscription.planComparison.freePlan.features.devices')}
                                </li>
                                <li>
                                  <FontAwesomeIcon icon={faCheckCircle} className="feature-icon text-success" />
                                  {t('settings.subscription.planComparison.freePlan.features.parts')}
                                </li>
                              </ul>
                              {subscription.plan === 'free' && (
                                <div className="current-plan-badge">
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                  {t('settings.subscription.planComparison.freePlan.currentPlan')}
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={6}>
                          <Card className={`plan-card premium-plan ${subscription.plan === 'premium' ? 'current-plan' : ''}`}>
                            <Card.Header>
                              <h5 className="mb-0">{t('settings.subscription.planComparison.premiumPlan.title')}</h5>
                              <div className="plan-price">{formatPrice(subscriptionPrice)} <span className="price-period">{t('settings.subscription.planComparison.premiumPlan.period')}</span></div>
                            </Card.Header>
                            <Card.Body>
                              <ul className="plan-features">
                                <li>
                                  <FontAwesomeIcon icon={faCheckCircle} className="feature-icon text-success" />
                                  {t('settings.subscription.planComparison.premiumPlan.features.unlimitedDevices')}
                                </li>
                                <li>
                                  <FontAwesomeIcon icon={faCheckCircle} className="feature-icon text-success" />
                                  {t('settings.subscription.planComparison.premiumPlan.features.unlimitedParts')}
                                </li>
                                <li>
                                  <FontAwesomeIcon icon={faCheckCircle} className="feature-icon text-success" />
                                  {t('settings.subscription.planComparison.premiumPlan.features.prioritySupport')}
                                </li>
                                <li>
                                  <FontAwesomeIcon icon={faCheckCircle} className="feature-icon text-success" />
                                  {t('settings.subscription.planComparison.premiumPlan.features.advancedStats')}
                                </li>
                              </ul>
                              {subscription.plan === 'premium' ? (
                                <div className="current-plan-badge">
                                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                  {t('settings.subscription.planComparison.premiumPlan.currentPlan')}
                                </div>
                              ) : (
                                <Button 
                                  variant="primary" 
                                  className="w-100 mt-3" 
                                  onClick={handleUpgrade}
                                  disabled={!functionsAvailable}
                                >
                                  <FontAwesomeIcon icon={faCreditCard} className="me-2" />
                                  {t('settings.subscription.buttons.upgrade')}
                                </Button>
                              )}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  </Tab.Pane>
                  <Tab.Pane active={activeTab === 'code'}>
                    <ActivationCodeForm onSuccess={handleActivationSuccess} />
                  </Tab.Pane>
                </Tab.Content>
              </Card.Body>
            </Card>
          )}
        </>
      ) : (
        <Alert variant="warning" className="subscription-alert">
          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
          {t('settings.subscription.alerts.noSubscription')}
        </Alert>
      )}
      
      {/* Modal di checkout Stripe */}
      <StripeCheckoutModal 
        show={showCheckoutModal} 
        onHide={() => setShowCheckoutModal(false)} 
        onSuccess={handleSuccessfulPayment}
        subscriptionPrice={subscriptionPrice}
      />
    </div>
  );
}

export default Subscription; 